import { Button, InputAdornment, TextField, Theme, Box } from '@mui/material';
import { Icon24InfoLocation } from '@care/react-icons';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { SxClassProps } from '@/types/common';
import { SEOSegmentationPageType } from '@/__generated__/globalTypes';
import { getZipcodeFromGeoInfo } from '@/utils/getZipcodeFromGeoInfo';
import { usePageAttributes } from '@/components/contexts/PageAttributesContext';
import { trackAmplitudeEvent, AmplitudeEvent } from '@/lib/analyticsHelper';
import { LaunchDarklyEvaluationReasons, UI_FEATURE_FLAGS, ServiceIdForMember } from '@/constants';
import useFeatureFlag from '@/components/hooks/useFeatureFlag';
import { useHeroContent } from './HeroContentContext';

const classes: SxClassProps = {
  textFieldOverride: (theme: Theme) => ({
    width: {
      xs: '100%',
      sm: '360px',
    },
    mb: {
      xs: 2,
      md: 3,
    },
    py: 1,

    '& .MuiInputAdornment-root': {
      ml: 0,
    },

    '& .MuiInputBase-root': {
      minHeight: '62px',
    },

    // removes number arrows from Chrome, Safari, Edge, Opera
    '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
      appearance: 'none',
      margin: 0,
    },
    // removes number arrows from Firefox
    '& input[type=text]': {
      cursor: 'auto',
      padding: theme.spacing(2, 2, 2, 0),
    },
  }),
};

const ZipCodeCTA = () => {
  const { enrollmentURL, directoryFirstExperience, userGeoInfo } = useHeroContent();
  const { pageType, serviceIdForMember } = usePageAttributes();
  const zipCodeRegex = /^\d{0,5}$/;
  const zipInputRef = useRef<HTMLInputElement | null>(null);

  const prefillZipCode = (): string => {
    if (pageType === SEOSegmentationPageType.NATIONAL) {
      const match = userGeoInfo?.match(/zip=(\d{5})/);
      if (match && match[1]) {
        return match[1];
      }
    }
    return '';
  };
  const prefillZipCodeForDirectoryFirst = () => {
    return getZipcodeFromGeoInfo(userGeoInfo);
  };

  const prefilledZipCode: string = prefillZipCode();

  const [zip, setZip] = useState({
    zipCode: prefilledZipCode,
  });

  useEffect(() => {
    if (zipInputRef.current) {
      zipInputRef.current.setSelectionRange(
        zipInputRef.current.value.length,
        zipInputRef.current.value.length
      );

      zipInputRef.current.focus();
    }
  }, [zip]);

  const onZipCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const zipCodeVal = e.target.value;

    if (!zipCodeRegex.test(zipCodeVal)) {
      return;
    }
    setZip((prevValues) => ({
      ...prevValues,
      zipCode: zipCodeVal,
    }));
  };

  const enrollmentLink = (() => {
    if (directoryFirstExperience) {
      if (zip.zipCode && zip.zipCode.length === 5) {
        return `${enrollmentURL}&zipcode=${zip.zipCode}`;
      }
      return `${enrollmentURL}&zipcode=${prefillZipCodeForDirectoryFirst()}`;
    }
    if (!directoryFirstExperience && zip.zipCode) {
      return `${enrollmentURL}&zipcode=${zip.zipCode}`;
    }
    return `${enrollmentURL}`;
  })();

  const daycareDirectoryFirstFlag = useFeatureFlag(
    UI_FEATURE_FLAGS.DAYCARE_DIRECTORY_FIRST_FLOW.id
  );

  const hideGetStartedCta =
    serviceIdForMember === ServiceIdForMember.CHILD_CARE_LOCATION ||
    serviceIdForMember === ServiceIdForMember.CHILD_CARE;

  const getCtaText = () => {
    if (hideGetStartedCta) return 'Start matching';
    return 'Get Started';
  };

  const handleCtaBtn = () => {
    const amplitudeData = {
      cta_text: getCtaText(),
      cta_type: 'button',
    };

    if (
      daycareDirectoryFirstFlag &&
      daycareDirectoryFirstFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.DAYCARE_DIRECTORY_FIRST_FLOW.id,
        test_variant: daycareDirectoryFirstFlag.variationIndex,
        service_id_for_member: serviceIdForMember,
        page_template: pageType,
      });
    }

    trackAmplitudeEvent('CTA Interacted', amplitudeData);
  };

  const handleKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      globalThis.location.href = `${enrollmentLink}`;
      handleCtaBtn();
    }
  };

  return (
    <Box display="flex" flexDirection="column" maxWidth={{ xs: '100%', sm: '360px' }}>
      <TextField
        id="zipCode"
        name="zipCode"
        inputRef={zipInputRef}
        onKeyUp={handleKeyUp}
        placeholder="ZIP code"
        value={zip.zipCode}
        onChange={onZipCodeChange}
        variant="standard"
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{
                mr: {
                  xs: 1,
                },
                fontWeight: 400,
              }}>
              <Icon24InfoLocation />
            </InputAdornment>
          ),
        }}
        sx={classes.textFieldOverride}
      />
      <Button
        sx={{
          width: '100%',
        }}
        color="primary"
        component="a"
        href={enrollmentLink}
        onClick={handleCtaBtn}
        size="large"
        variant="contained"
        rel="nofollow">
        {getCtaText()}
      </Button>
    </Box>
  );
};

export { ZipCodeCTA };
